import React, { useEffect, useRef, forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { canHandleOutside } from '../../utils/click-outside.util';

/**
 * @typedef Props
 * @prop  {React.ReactNode} children
 * @prop  {string} [className]
 * @prop {boolean} [open]
 * @prop {'bottom-left' | 'bottom-right'} [placement]
 * @prop {React.ReactNode} trigger
 * @prop {string} [id]
 * @prop {() => void} [handleClickOutside]
 * @prop {string} [ariaLabelledby]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifDropdown = (props) => {
  const {
    children,
    open = false,
    className = '',
    placement = 'bottom-right',
    trigger = null,
    id = null,
    handleClickOutside = () => null,
    ariaLabelledby = null,
    ...other
  } = props;
  const dropdown = useRef(null);
  const triggerRef = useRef();
  const [dropUp, setDropUp] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [currentItem, setCurrentItem] = useState(-1);

  const itemsChildren = React.Children.map(children, (child, index) =>
    React.cloneElement(child, {
      indexItem: index,
      currentItem
    })
  );

  const triggerButton = React.Children.map(trigger, (child) =>
    React.cloneElement(child, {
      ref: triggerRef,
      'aria-expanded': openDropdown,
      'aria-haspopup': 'true',
      'aria-controls': id
    })
  );

  useEffect(() => {
    setOpenDropdown(open);
  }, [open]);

  useEffect(() => {
    if (dropdown && dropdown.current && open) {
      const rect = dropdown.current.getBoundingClientRect();
      if (rect.bottom > window.innerHeight) {
        setDropUp(true);
      } else {
        setDropUp(false);
      }
    }
  }, [open]);

  const classes = classNames({
    'motif-dropdown': true,
    'motif-dropdown-bottom-left': placement === 'bottom-left',
    'motif-dropdown-open': openDropdown,
    'motif-dropdown-drop-up': dropUp,
    [className]: true
  });

  const target = useRef(null);

  const clickOutside = event => {
    if (canHandleOutside(target, event)) {
      setDropUp(false);
      handleClickOutside(id);
    }
  };

  useEffect(() => {
    document.addEventListener('click', clickOutside);

    return () => document.removeEventListener('click', clickOutside);
  }, []);

  const handleKeyDown = event => {
    const pressedKey = event.key;

    switch (pressedKey) {
      case 'ArrowUp':
        event.preventDefault();
        if (currentItem > 0) {
          setCurrentItem(currentItem - 1);
        } else {
          setCurrentItem(itemsChildren.length - 1);
        }
        break;

      case 'ArrowDown':
        event.preventDefault();
        if (currentItem < itemsChildren.length - 1) {
          setCurrentItem(currentItem + 1);
        } else {
          setCurrentItem(0);
        }
        break;

      case 'Escape':
        event.preventDefault();
        setOpenDropdown(false);
        if (triggerRef.current) {
          triggerRef.current.focus();
        }
        break;

      case 'Enter':
        event.preventDefault();
        setOpenDropdown(true);
        break;

      case 'Tab':
        setOpenDropdown(false);
        break;

      default:
        break;
    }
  };

  return (
    <div ref={target} className={classes} onKeyDown={handleKeyDown} {...other} data-motif-dropdown>
      <div className="motif-dropdown-trigger">{triggerButton}</div>
      <div
        className="motif-dropdown-menu"
        style={{
          display: openDropdown ? 'block' : 'none',
          top: dropUp
            ? `${-(dropdown.current.getBoundingClientRect().height + 20) / 14}rem`
            : '100%'
        }}
        x-placement={placement}
        id={id}
        role="menu"
        aria-labelledby={ariaLabelledby}
        ref={dropdown}
      >
        {itemsChildren}
      </div>
    </div>
  );
};

MotifDropdown.propTypes = {
  children: PropTypes.node,
  trigger: PropTypes.node,
  className: PropTypes.string,
  id: PropTypes.string,
  ariaLabelledby: PropTypes.string,
  handleClickOutside: PropTypes.func,
  open: PropTypes.bool,
  placement: PropTypes.oneOf(['bottom-left', 'bottom-right'])
};

export { MotifDropdown };
